
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const PrivacyPolicyEn = () => {
  const { t } = useTranslation(['common']);

  return (
    <div className="min-h-screen bg-uplivion-lightBlue/30 py-8">
      <div className="container mx-auto px-6 max-w-4xl">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="mb-8">
            <Link to="/" className="text-uplivion-blue hover:underline mb-4 inline-block">
              ← Back to Home
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
            <p className="text-gray-600">Effective from: June 28, 2025 | Version 1.1</p>
          </div>

          <div className="prose max-w-none">
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-8">
              <div className="flex">
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    <strong>GDPR Compliant:</strong> This privacy policy complies with the requirements 
                    of the EU General Data Protection Regulation (GDPR) and provides comprehensive 
                    information about the processing of your personal data on uplivion.
                  </p>
                </div>
              </div>
            </div>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">1. Data Controller</h2>
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="mb-2">
                  <strong>Responsible for data processing:</strong>
                </p>
                <p className="mb-2">
                  <strong>Arc Rider Ventures GmbH</strong><br/>
                  Jänickendorfer Str. 64a<br/>
                  14943 Brandenburg<br/>
                  Germany
                </p>
                <p className="mb-2">
                  <strong>Commercial Register:</strong> Local Court Berlin Charlottenburg HRB199081B<br/>
                  <strong>Managing Director:</strong> Jakob Jordan
                </p>
                <p>
                  <strong>Email:</strong> support@uplivion.life<br/>
                  <strong>Data Protection Officer:</strong> support@uplivion.life
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">2. Data Collected</h2>
              <h3 className="text-xl font-semibold mb-3">2.1 Registration Data</h3>
              <ul className="list-disc pl-6 mb-4">
                <li>Email address (required field)</li>
                <li>Username (required field)</li>
                <li>First and last name (required field)</li>
                <li>Date of birth (required field)</li>
                <li>Password (encrypted storage)</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3">2.2 Timeline Content</h3>
              <ul className="list-disc pl-6 mb-4">
                <li>Texts and descriptions of events</li>
                <li>Uploaded photos and images</li>
                <li>Date and time information of events</li>
                <li>Categories and tags</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3">2.3 Technical Data</h3>
              <ul className="list-disc pl-6 mb-4">
                <li>IP address (temporarily for security)</li>
                <li>Browser information</li>
                <li>Device information</li>
                <li>Usage statistics (anonymized)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">3. Purposes of Data Processing</h2>
              <h3 className="text-xl font-semibold mb-3">3.1 Contract Performance (Art. 6 Para. 1 lit. b GDPR)</h3>
              <ul className="list-disc pl-6 mb-4">
                <li>Provision of timeline functions</li>
                <li>User account management</li>
                <li>Storage and display of your content</li>
                <li>Friends functions and sharing</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3">3.2 Legitimate Interests (Art. 6 Para. 1 lit. f GDPR)</h3>
              <ul className="list-disc pl-6 mb-4">
                <li>Service improvement (beta testing)</li>
                <li>Security and abuse prevention</li>
                <li>Technical optimization</li>
                <li>Customer support</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3">3.3 Consent (Art. 6 Para. 1 lit. a GDPR)</h3>
              <ul className="list-disc pl-6 mb-4">
                <li>Marketing emails (optional)</li>
                <li>Extended analytics (optional)</li>
                <li>Newsletter (optional)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">4. Technical Infrastructure and Hosting</h2>
              <p className="mb-4">
                uplivion exclusively uses European service providers for GDPR-compliant 
                and secure data processing:
              </p>

              <div className="grid md:grid-cols-1 gap-4 mb-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-semibold text-green-800 mb-2">🇪🇺 Fully European Infrastructure</h4>
                  <p className="text-sm text-green-700">
                    All your data is processed and stored exclusively within the EU/EEA.
                  </p>
                </div>
              </div>

              <h3 className="text-xl font-semibold mb-3">4.1 Website Hosting (Netlify)</h3>
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="mb-2"><strong>Provider:</strong> Netlify, Inc.</p>
                <p className="mb-2"><strong>Location:</strong> European CDN Edge Locations (Frankfurt, London)</p>
                <p className="mb-2"><strong>Purpose:</strong> Website hosting and content delivery</p>
                <p className="mb-2"><strong>Data Processed:</strong> IP address, browser information, access logs</p>
                <p className="mb-2"><strong>Legal Basis:</strong> Art. 6 Para. 1 lit. f GDPR (legitimate interests)</p>
                <p className="text-sm text-gray-600">
                  Netlify ensures that European users are served via European servers. 
                  Data Processing Agreement (DPA) according to Art. 28 GDPR is in place.
                </p>
              </div>

              <h3 className="text-xl font-semibold mb-3">4.2 Database Services (Supabase)</h3>
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="mb-2"><strong>Provider:</strong> Supabase, Inc.</p>
                <p className="mb-2"><strong>Location:</strong> Germany (Frankfurt)</p>
                <p className="mb-2"><strong>Purpose:</strong> Database hosting, authentication, real-time services</p>
                <p className="mb-2"><strong>Data Processed:</strong> All user data, timeline content, authentication data</p>
                <p className="mb-2"><strong>Legal Basis:</strong> Art. 6 Para. 1 lit. b GDPR (contract performance)</p>
                <p className="text-sm text-gray-600">
                  All database servers are located in Germany. Supabase is SOC 2 Type II certified 
                  and meets GDPR requirements.
                </p>
              </div>

              <h3 className="text-xl font-semibold mb-3">4.3 Email Services (Resend)</h3>
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="mb-2"><strong>Provider:</strong> Resend Technologies Limited</p>
                <p className="mb-2"><strong>Location:</strong> Ireland (Dublin)</p>
                <p className="mb-2"><strong>Purpose:</strong> Transactional emails, notifications</p>
                <p className="mb-2"><strong>Data Processed:</strong> Email addresses, email content, delivery statistics</p>
                <p className="mb-2"><strong>Legal Basis:</strong> Art. 6 Para. 1 lit. b GDPR (contract performance)</p>
                <p className="text-sm text-gray-600">
                  Resend operates entirely within the EU and meets all GDPR requirements 
                  for email service providers.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">5. Data Processors and Data Sharing</h2>
              <p className="mb-4">
                Your data is only shared with the following data processors, 
                all of which have GDPR-compliant data processing agreements:
              </p>

              <div className="space-y-4 mb-6">
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Netlify (Website Hosting)</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• European CDN infrastructure</li>
                    <li>• ISO 27001 and SOC 2 certified</li>
                    <li>• GDPR-compliant data processing</li>
                    <li>• No third-country transfers for EU users</li>
                  </ul>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Supabase (Database)</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Server location: Germany (Frankfurt)</li>
                    <li>• SOC 2 Type II and ISO 27001 certified</li>
                    <li>• End-to-end encryption</li>
                    <li>• Complete EU data residency</li>
                  </ul>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Resend (Email Service)</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Irish legal basis (EU)</li>
                    <li>• GDPR-compliant email delivery</li>
                    <li>• Encrypted data transmission</li>
                    <li>• Automatic deletion of email logs</li>
                  </ul>
                </div>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-semibold text-red-800 mb-2">No sharing with:</h4>
                <ul className="text-sm text-red-700 space-y-1">
                  <li>• Third parties for advertising purposes</li>
                  <li>• Non-EU service providers without adequacy decision</li>
                  <li>• Social media platforms</li>
                  <li>• Data brokers or analytics providers</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">6. Data Security</h2>
              <p className="mb-4">
                We use state-of-the-art security technologies and procedures:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>Encryption:</strong> TLS 1.3 for all data transmissions</li>
                <li><strong>Storage Encryption:</strong> AES-256 for all stored data</li>
                <li><strong>Access Control:</strong> Multi-factor authentication for all systems</li>
                <li><strong>Monitoring:</strong> 24/7 security monitoring</li>
                <li><strong>Backups:</strong> Encrypted daily backups in EU data centers</li>
                <li><strong>Certifications:</strong> All partners are ISO 27001 and/or SOC 2 certified</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">7. Your Rights</h2>
              <p className="mb-4">You have the following rights regarding your data:</p>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>Right of Access (Art. 15 GDPR):</strong> Information about stored data</li>
                <li><strong>Right to Rectification (Art. 16 GDPR):</strong> Correction of incorrect data</li>
                <li><strong>Right to Erasure (Art. 17 GDPR):</strong> Deletion of your data</li>
                <li><strong>Right to Restriction (Art. 18 GDPR):</strong> Limitation of processing</li>
                <li><strong>Right to Data Portability (Art. 20 GDPR):</strong> Export of your data</li>
                <li><strong>Right to Object (Art. 21 GDPR):</strong> Objection to processing</li>
                <li><strong>Right to Withdraw (Art. 7 GDPR):</strong> Withdrawal of given consent</li>
              </ul>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-blue-800 mb-2">Exercising Your Rights</h4>
                <p className="text-sm text-blue-700 mb-2">
                  To process your requests, please contact us at:
                </p>
                <p className="text-sm text-blue-700">
                  <strong>All data protection requests:</strong> support@uplivion.life
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">8. Data Retention</h2>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>Registration data:</strong> Until account deletion</li>
                <li><strong>Timeline content:</strong> Until deletion by you or your account</li>
                <li><strong>Technical logs:</strong> Maximum 30 days (Netlify, Supabase)</li>
                <li><strong>Email logs:</strong> Maximum 7 days (Resend)</li>
                <li><strong>Backup data:</strong> Maximum 90 days after deletion</li>
                <li><strong>Marketing consents:</strong> Until withdrawal</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">9. Cookies and Tracking</h2>
              <p className="mb-4">
                uplivion only uses technically necessary cookies:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>Session cookies:</strong> For login and navigation</li>
                <li><strong>Settings cookies:</strong> For your preferences (language, etc.)</li>
                <li><strong>Security cookies:</strong> Protection against abuse</li>
              </ul>
              <p className="mb-4">
                <strong>No tracking cookies</strong> from third parties without your consent.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">10. International Data Transfers</h2>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-green-800 mb-2">✅ No Third-Country Transfers</h4>
                <p className="text-sm text-green-700">
                  All your data is processed exclusively within the EU/EEA. 
                  No transfers to third countries take place.
                </p>
              </div>
              
              <ul className="list-disc pl-6 mb-4">
                <li><strong>Germany:</strong> Supabase database (Frankfurt)</li>
                <li><strong>Ireland:</strong> Resend email service (Dublin)</li>
                <li><strong>EU CDN:</strong> Netlify edge locations (Frankfurt, London)</li>
              </ul>
              
              <p className="mb-4">
                All mentioned locations are subject to the GDPR and offer the same 
                level of data protection as Germany.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">11. Beta Phase Specifics</h2>
              <p className="mb-4">
                During the beta phase, special provisions apply:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>Extended logs:</strong> Anonymized debug information for max. 7 days</li>
                <li><strong>Feedback collection:</strong> Voluntary participation in improvement suggestions</li>
                <li><strong>Performance monitoring:</strong> Anonymized usage statistics</li>
                <li><strong>Data migrations:</strong> Timely information about system updates</li>
              </ul>
              
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <h4 className="font-semibold text-orange-800 mb-2">Beta Phase Guarantees</h4>
                <ul className="text-sm text-orange-700 space-y-1">
                  <li>• All GDPR rights remain fully intact</li>
                  <li>• No relaxation of data protection standards</li>
                  <li>• Additional transparency about data processing</li>
                  <li>• Free data exports available at any time</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">12. Right to Complain</h2>
              <p className="mb-4">
                You have the right to file a complaint with a data protection supervisory authority 
                about the processing of your data:
              </p>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Competent Authority (Germany)</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    <strong>The State Commissioner for Data Protection and Freedom of Information Brandenburg</strong>
                  </p>
                  <p className="text-sm text-gray-600">
                    Stahnsdorfer Damm 77<br/>
                    14532 Kleinmachnow<br/>
                    Email: poststelle@lda.brandenburg.de
                  </p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">EU Level</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    You can also contact the data protection authority of your place of residence or 
                    file a complaint with the EU Commission.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">13. Contact & Data Protection Officers</h2>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold mb-2">Data Controller</h4>
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>Arc Rider Ventures GmbH</strong><br/>
                      Jänickendorfer Str. 64a<br/>
                      14943 Brandenburg<br/>
                      Germany
                    </p>
                    <p className="text-sm text-gray-600">
                      <strong>Managing Director:</strong> Jakob Jordan<br/>
                      <strong>Commercial Register:</strong> HRB199081B (Local Court Berlin Charlottenburg)
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold mb-2">Data Protection Contacts</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li><strong>General:</strong> support@uplivion.life</li>
                      <li><strong>Data Protection Officer:</strong> dpo@uplivion.life</li>
                      <li><strong>Deletion requests:</strong> delete@uplivion.life</li>
                      <li><strong>Data export:</strong> export@uplivion.life</li>
                      <li><strong>Complaints:</strong> complaints@uplivion.life</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>
          </div>

          <div className="mt-8 pt-8 border-t">
            <Button asChild>
              <Link to="/">Back to Home</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyEn;
